import { useQuery } from '@tanstack/react-query';
import { Card } from '../../ui';
import { listLibraryItems } from '../../shared/queries';
import { supabase } from '../../shared/supabase/client';
import { useInstance } from '../instance/InstanceProvider';
import { text } from '../../shared/text';
import { ClientGuard } from './ClientGuard';
import { AppBar, ClientPage } from './ClientPage';
import { CoachVideo } from './CoachVideo';

/**
 * Her coach's recordings, browsable outside a session.
 *
 * ## Why this screen exists at all
 *
 * To answer one question cheaply: **would a client ever open a library of her
 * videos, or do videos only matter inside a session?** `mvp-discovery` → D17
 * sizes the video subsystem at effort M — a private bucket, resumable upload,
 * browser recording that refuses formats phones cannot play, poster frames,
 * signed URLs, an EU backup — and it is the largest unbuilt piece of
 * infrastructure in the product. Building it before anyone knows whether the
 * library is opened is the most expensive available order. This screen costs a
 * screen (`client-coach-presence` design, D1).
 *
 * ## Only her, and the database is what makes that true
 *
 * There is **one coach per instance**, enforced by the partial unique index
 * `profile_one_coach_per_instance`, and `coach_id` *is* `coach_instance.id`. So
 * every row `listLibraryItems` can return belongs to this client's own coach,
 * and the credit line on this screen comes from `coach_instance.coach_display_name`
 * — there is no author field on an item and no array of coaches anywhere.
 *
 * The hi-fi for this screen credits its mock videos to two different coaches.
 * That is an artefact of a mock and it must never become a data shape;
 * `coachPresence.test.tsx` fails if either of those names, or any second coach,
 * appears here.
 *
 * ## What the list actually contains, which is narrower than "her library"
 *
 * `exercise_library_item_select_client_referenced` lets a client read only the
 * items one of **her own published entries** points at — not her coach's whole
 * library, because an exercise she has never been given is not hers to browse.
 * So this is "the recordings behind the exercises you have been given", and the
 * screen says so rather than promising a catalogue it cannot show. That is the
 * policy's narrowing rather than this screen's, and it is the more careful of
 * the two readings.
 *
 * ## What is hollow here, said out loud
 *
 * **No video file exists.** There is no storage bucket in any migration, and
 * nothing in this change creates, uploads or plays one; `./CoachVideo.tsx` is
 * where that is admitted, in one sentence, to the client. In a real build this
 * screen is empty, because no coach has ever recorded anything.
 */
export function VideosPage() {
  return (
    <ClientGuard>
      <Videos />
    </ClientGuard>
  );
}

function Videos() {
  const instance = useInstance();

  /*
   * The same query the session screen's sibling uses, and no filter of its own:
   * which items a client may see is the policy's decision, not this screen's.
   * What is filtered here is only "has a recording", which is a property of the
   * row rather than a question of access.
   */
  const items = useQuery({
    queryKey: ['library-items', 'all'],
    queryFn: () => listLibraryItems(supabase),
  });

  const videos = (items.data ?? []).filter((item) => item.video_path !== null);

  return (
    <ClientPage>
      <AppBar backTo={`/c/${instance.slug}/today`} backLabel={text('client.videos.back')} />

      <h1 className="text-ink font-display mt-2 text-3xl leading-tight">
        {text('client.videos.title')}
      </h1>
      <p className="text-ink-muted mt-1 text-sm">
        {text('client.videos.intro', { coach: instance.coachDisplayName })}
      </p>

      {items.isPending ? (
        <p className="text-ink-muted mt-4 text-sm">{text('app.loading')}</p>
      ) : null}

      {/*
        An empty library says so, in a sentence. Not an empty grid, not a row of
        grey rectangles waiting for something, and not a spinner that never
        resolves: the screen has an answer and gives it.
      */}
      {!items.isPending && videos.length === 0 ? (
        <Card className="mt-4">
          <h2 className="text-ink font-display text-xl">{text('client.videos.emptyTitle')}</h2>
          <p className="text-ink-soft mt-2 text-sm leading-relaxed">
            {text('client.videos.emptyBody', { coach: instance.coachDisplayName })}
          </p>
        </Card>
      ) : null}

      {videos.length === 0 ? null : (
        <ul className="mt-4 flex flex-col gap-3">
          {videos.map((item) => (
            <li key={item.id}>
              <Card>
                {/*
                  Which exercise it demonstrates is the identity of the video —
                  there is no separate video title, because the recording is of
                  the movement and nothing else.
                */}
                <p className="text-ink font-medium">{item.name}</p>
                {item.cue === null || item.cue.trim() === '' ? null : (
                  <p className="text-ink-soft mt-1 text-sm leading-relaxed">{item.cue}</p>
                )}
                {/*
                  The same control, in the same words, as the one inside a
                  session — so opening a recording never depends on being in one.
                */}
                <CoachVideo coach={instance.coachDisplayName} className="mt-2" />
              </Card>
            </li>
          ))}
        </ul>
      )}
    </ClientPage>
  );
}
